import React from 'react';
import { Field } from 'react-final-form';
// Import config and utils
import { useIntl } from '../../util/reactIntl';
import {
  SCHEMA_TYPE_ENUM,
  SCHEMA_TYPE_MULTI_ENUM,
  SCHEMA_TYPE_TEXT,
  SCHEMA_TYPE_LONG,
  SCHEMA_TYPE_BOOLEAN,
  SCHEMA_TYPE_YOUTUBE,
} from '../../util/types';
import {
  required,
  nonEmptyArray,
  validateInteger,
  validateYoutubeURL,
} from '../../util/validators';
// Import shared components
import { FieldCheckboxGroup, FieldSelect, FieldTextInput, FieldBoolean } from '../../components';
// Import modules from this directory
import css from './CustomExtendedDataField.module.css';

const createFilterOptions = options => options.map(o => ({ key: `${o.option}`, label: o.label }));

const getLabel = fieldConfig => fieldConfig?.saveConfig?.label || fieldConfig?.label;

const CustomFieldEnum = props => {
  const { name, fieldConfig, defaultRequiredMessage, formId, intl } = props;
  const { enumOptions = [], saveConfig } = fieldConfig || {};
  const { placeholderMessage, isRequired, requiredMessage } = saveConfig || {};
  const validateMaybe = isRequired
    ? { validate: required(requiredMessage || defaultRequiredMessage) }
    : {};
  const placeholder =
    placeholderMessage ||
    intl.formatMessage({ id: 'CustomExtendedDataField.placeholderSingleSelect' });
  const filterOptions = createFilterOptions(enumOptions);

  const label = getLabel(fieldConfig);

  return filterOptions ? (
  <>
    <FieldSelect
      className={css.customField}
      name={name}
      id={formId ? `${formId}.${name}` : name}
      label={label}
      {...validateMaybe}
    >
      <option disabled value="">
        {placeholder}
      </option>
      {filterOptions.map(optionConfig => {
        const key = optionConfig.key;
        return (
          <option key={key} value={key}>
            {optionConfig.label}
          </option>
        );
      })}
    </FieldSelect>
        {name === 'priv_professional_indemnity_insurance' && (
            <Field name={name}>
            {({ input }) =>
                input.value === 'no' ? (
                <p className={css.piiHelpText}>
                    {intl.formatMessage({ id: 'CustomExtendedDataField.piiObtainText' })}{' '}
                    <a href="https://www.kingsbridge.co.uk/allegro/" target="_blank" rel="noopener noreferrer">
                    {intl.formatMessage({ id: 'CustomExtendedDataField.piiObtainLinkText' })}
                    </a>
                </p>
                ) : null
            }
            </Field>
        )}
      </>
  ) : null;
};

  const CustomFieldMultiEnum = props => {
    const { name, fieldConfig, defaultRequiredMessage, formId } = props;
    const { enumOptions = [], saveConfig } = fieldConfig || {};
    const { isRequired, requiredMessage } = saveConfig || {};
    const label = getLabel(fieldConfig);
    const validateMaybe = isRequired
      ? { validate: nonEmptyArray(requiredMessage || defaultRequiredMessage) }
      : {};
    const otherFieldName = name.replace(/^pub_/, 'pub_other_');
  
    return enumOptions ? (
      <Field name={name}>
        {({ input }) => (
          <>
            <FieldCheckboxGroup
              className={css.customField}
              id={formId ? `${formId}.${name}` : name}
              name={name}
              label={label}
              options={createFilterOptions(enumOptions)}
              {...validateMaybe}
            />
            {Array.isArray(input.value) && input.value.some(v => ['other', 'us_state', 'australian_state'].includes(v)) && (
              <FieldTextInput
                className={css.customField}
                id={formId ? `${formId}.${otherFieldName}` : otherFieldName}
                name={otherFieldName}
                type="text"
                label={`Please specify`}
                placeholder={`Enter your ${label?.toLowerCase() || 'value'}`}
              />
            )}
          </>
        )}
      </Field>
    ) : null;
  };

  const CustomFieldText = props => {
    const { name, fieldConfig, defaultRequiredMessage, formId, intl } = props;
    const { placeholderMessage, isRequired, requiredMessage } = fieldConfig?.saveConfig || {};
    const label = getLabel(fieldConfig);
    const validateMaybe = isRequired
      ? { validate: required(requiredMessage || defaultRequiredMessage) }
      : {};
    const placeholder =
      placeholderMessage || intl.formatMessage({ id: 'CustomExtendedDataField.placeholderText' });
  
    const validateYearOfQualification = value => {
      if (!value) return undefined;
      return /^\d{4}$/.test(value) ? undefined : 'Must be a 4-digit year';
    };
  
    const validateWebsiteURL = value => {
      if (!value) return undefined;
      return /^https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z]{2,}(\/.*)?$/.test(value)
        ? undefined
        : 'Must be a valid website URL (e.g. https://example.com)';
    };
  
    const validateLinkedInURL = value => {
      if (!value) return undefined;
      return /^https?:\/\/(www\.)?linkedin\.com\/in\/[a-zA-Z0-9_-]+\/?$/.test(value)
        ? undefined
        : 'Must be a valid LinkedIn profile URL (e.g. https://linkedin.com/in/yourname)';
    };
  
    if (name === 'pub_linkedin') {
      return (
        <FieldTextInput
          className={css.customField}
          id={formId ? `${formId}.${name}` : name}
          name={name}
          type="text"
          label={label}
          placeholder={placeholder}
          validate={validateLinkedInURL}
          showErrorOnChange
        />
      );
    }
  
    if (name === 'pub_website') {
      return (
        <FieldTextInput
          className={css.customField}
          id={formId ? `${formId}.${name}` : name}
          name={name}
          type="text"
          label={label}
          placeholder={placeholder}
          validate={validateWebsiteURL}
          showErrorOnChange
        />
      );
    }
  
    if (name === 'pub_year_of_qualification') {
      return (
        <FieldTextInput
          className={css.customField}
          id={formId ? `${formId}.${name}` : name}
          name={name}
          type="text"
          label={label}
          placeholder={placeholder}
          validate={validateYearOfQualification}
          showErrorOnChange
        />
      );
    }
  
    if (
      name === 'pub_headline' ||
      name === 'pub_authorisation_number' ||
      name === 'pub_company_name_lawyer' ||
      name === 'pub_company' ||
      name === 'pub_job_title'
    ) {
      return (
        <FieldTextInput
          className={css.customField}
          id={formId ? `${formId}.${name}` : name}
          name={name}
          type="text"
          label={label}
          placeholder={placeholder}
          {...validateMaybe}
        />
      );
    }
  
    return (
      <FieldTextInput
        className={css.customField}
        id={formId ? `${formId}.${name}` : name}
        name={name}
        type="textarea"
        label={label}
        placeholder={placeholder}
        {...validateMaybe}
      />
    );
  };

const CustomFieldLong = props => {
  const { name, fieldConfig, defaultRequiredMessage, formId, intl } = props;
  const { minimum, maximum, saveConfig } = fieldConfig;
  const { placeholderMessage, isRequired, requiredMessage } = saveConfig || {};
  const label = getLabel(fieldConfig);
  const placeholder =
    placeholderMessage || intl.formatMessage({ id: 'CustomExtendedDataField.placeholderLong' });
  const numberTooSmallMessage = intl.formatMessage(
    { id: 'CustomExtendedDataField.numberTooSmall' },
    { min: minimum }
  );
  const numberTooBigMessage = intl.formatMessage(
    { id: 'CustomExtendedDataField.numberTooBig' },
    { max: maximum }
  );

  // Field with schema type 'long' will always be validated against min & max
  const validate = (value, min, max) => {
    const requiredMsg = requiredMessage || defaultRequiredMessage;
    return isRequired && value == null
      ? requiredMsg
      : validateInteger(value, max, min, numberTooSmallMessage, numberTooBigMessage);
  };

  return (
    <FieldTextInput
      className={css.customField}
      id={formId ? `${formId}.${name}` : name}
      name={name}
      type="number"
      step="1"
      parse={value => {
        const parsed = Number.parseInt(value, 10);
        return Number.isNaN(parsed) ? null : parsed;
      }}
      label={label}
      placeholder={placeholder}
      validate={value => validate(value, minimum, maximum)}
      onWheel={e => {
        // fix: number input should not change value on scroll
        if (e.target === document.activeElement) {
          // Prevent the input value change, because we prefer page scrolling
          e.target.blur();

          // Refocus immediately, on the next tick (after the current function is done)
          setTimeout(() => {
            e.target.focus();
          }, 0);
        }
      }}
    />
  );
};

const CustomFieldBoolean = props => {
  const { name, fieldConfig, defaultRequiredMessage, formId, intl } = props;
  const { placeholderMessage, isRequired, requiredMessage } = fieldConfig?.saveConfig || {};
  const label = getLabel(fieldConfig);
  const validateMaybe = isRequired
    ? { validate: required(requiredMessage || defaultRequiredMessage) }
    : {};
  const placeholder =
    placeholderMessage || intl.formatMessage({ id: 'CustomExtendedDataField.placeholderBoolean' });

  return (
    <FieldBoolean
      className={css.customField}
      id={formId ? `${formId}.${name}` : name}
      name={name}
      label={label}
      placeholder={placeholder}
      {...validateMaybe}
    />
  );
};

const CustomFieldYoutube = props => {
  const { name, fieldConfig, defaultRequiredMessage, formId, intl } = props;
  const { placeholderMessage, isRequired, requiredMessage } = fieldConfig?.saveConfig || {};
  const label = getLabel(fieldConfig);
  const placeholder =
    placeholderMessage ||
    intl.formatMessage({ id: 'CustomExtendedDataField.placeholderYoutubeVideoURL' });

  const notValidUrlMessage = intl.formatMessage({
    id: 'CustomExtendedDataField.notValidYoutubeVideoURL',
  });

  const validate = value => {
    const requiredMsg = requiredMessage || defaultRequiredMessage;
    return isRequired && value == null
      ? requiredMsg
      : validateYoutubeURL(value, notValidUrlMessage);
  };

  return (
    <FieldTextInput
      className={css.customField}
      id={formId ? `${formId}.${name}` : name}
      name={name}
      type="text"
      label={label}
      placeholder={placeholder}
      validate={value => validate(value)}
    />
  );
};

/**
 * Return Final Form field for each configuration according to schema type.
 *
 * These custom extended data fields are for generating input fields from configuration defined
 * in marketplace-custom-config.js. Other panels in EditListingWizard might add more extended data
 * fields (e.g. shipping fee), but these are independently customizable.
 *
 * @param {Object} props should contain fieldConfig that defines schemaType, enumOptions?, and
 * saveConfig for the field.
 */
const CustomExtendedDataField = props => {
    const intl = useIntl();
    const { enumOptions = [], schemaType } = props?.fieldConfig || {};
    const renderFieldComponent = (FieldComponent, props) => <FieldComponent {...props} intl={intl} />;
  
    const INLINE_OTHER_FIELDS = ['pub_other_sector', 'pub_other_specialism', 'pub_other_jurisdiction'];
    if (INLINE_OTHER_FIELDS.includes(props.name)) {
      return null;
    }
  
    return schemaType === SCHEMA_TYPE_ENUM && enumOptions
      ? renderFieldComponent(CustomFieldEnum, props)
      : schemaType === SCHEMA_TYPE_MULTI_ENUM && enumOptions
      ? renderFieldComponent(CustomFieldMultiEnum, props)
      : schemaType === SCHEMA_TYPE_TEXT
      ? renderFieldComponent(CustomFieldText, props)
      : schemaType === SCHEMA_TYPE_LONG
      ? renderFieldComponent(CustomFieldLong, props)
      : schemaType === SCHEMA_TYPE_BOOLEAN
      ? renderFieldComponent(CustomFieldBoolean, props)
      : schemaType === SCHEMA_TYPE_YOUTUBE
      ? renderFieldComponent(CustomFieldYoutube, props)
      : null;
  };

export default CustomExtendedDataField;
